import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import ListItemCardCst from "@components/ListItemCardCst";
import { ListItemBadge } from "@components/ListItemCardCst/types";
import { useCardActions } from "@hooks/useCardActions";
import { formatDate } from "@utils/utilsDate";
import { mapGroupToCard } from "../mapGroupToCard";
import { UGEntityType } from "../types";

export interface GroupItemHandlers {
  onEnroll: (group: any) => void;
  onViewLO: (group: any) => void;
  onEdit: (group: any) => void;
  onDelete: (group: any) => void;
}

interface GroupItemProps extends GroupItemHandlers {
  group: any;
  entityType: Extract<UGEntityType, "group-static" | "group-dynamic">;
  corporateId?: string;
  selectable?: boolean;
  selected?: boolean;
  onSelect?: (next: boolean) => void;
  onOpenDetail?: (group: any) => void;
  showCreationDate?: boolean;
}

const GroupItem: React.FC<GroupItemProps> = ({
  group,
  entityType,
  corporateId,
  selectable = true,
  selected = false,
  showCreationDate = false,
  onSelect,
  onEnroll,
  onViewLO,
  onEdit,
  onDelete,
  onOpenDetail,
}) => {
  const overrides = useMemo(
    () => ({
      enroll: () => onEnroll(group),
      "view-lo": () => onViewLO(group),
      edit: () => onEdit(group),
      delete: () => onDelete(group),
    }),
    [group, onEnroll, onViewLO, onEdit, onDelete],
  );

  const moreActions = useCardActions(entityType, group, {
    corporateId,
    overrides,
  });

  const { t: tCard } = useTranslation("list-item-card");
  const cardProps = mapGroupToCard(group);

  const badges = useMemo((): ListItemBadge[] => {
    const baseBadges = cardProps.badges || [];
    if (!showCreationDate) return baseBadges;
    const rawDate =
      group?.creation_date || group?.created_date || group?.createdAt;
    if (!rawDate || Number.isNaN(new Date(rawDate).getTime()))
      return baseBadges;
    return [
      ...baseBadges,
      {
        label: String(
          tCard("badges.created-on", {
            date: formatDate(rawDate),
            interpolation: { escapeValue: false },
          }),
        ),
      },
    ];
  }, [cardProps.badges, group, showCreationDate, tCard]);

  const actions = onOpenDetail
    ? [
        {
          label: tCard("actions.details"),
          variant: "outlined" as const,
          onClick: () => onOpenDetail(group),
        },
      ]
    : undefined;

  return (
    <ListItemCardCst
      {...cardProps}
      badges={badges}
      selectable={selectable}
      selected={selected}
      onSelect={onSelect}
      actions={actions}
      moreActions={moreActions}
    />
  );
};

export default GroupItem;
